import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { formatDateTime } from '@/lib/date-display.js';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';
import { getSharedLogin, setSharedLogin, sharedRoutesText, sharedOriginsText, confirmReasonLines } from '@/services/admin-users.service.js';

// THE SHARED-LOGIN SWITCH (personal-logins L5, PLAN 3 and R2): auth.shared_login_mode, read by its
// one reader (services/shared-login-switch.service.js) and written only through
// POST /api/tracon/admin/auth/shared-login, which the owner's own Google sign-in alone may call.
// The page renders the server's answer (a re-read after every write) and derives nothing. Turning
// it off is refused until an owner has signed in with Google (409 owner_google_unproven), and asks
// for a second, explicit yes when the shared login was used in the last 7 days or no owner has
// signed in on the installed app (409 confirm_required).

const MODES = [
  { mode: 'on', label: 'On', note: 'The shared password works, as today.' },
  { mode: 'notify', label: 'Notify', note: 'It still works; every use is recorded and David is told once a day.' },
  { mode: 'off', label: 'Off', note: 'The password form disappears and shared tokens stop working everywhere.' },
];
const labelOf = (mode) => (MODES.find((m) => m.mode === mode) || { label: mode }).label;
const WHY_WORDS = {
  row: '',
  absent: 'no row yet, read as on',
  env: 'forced on by the Render setting TRACON_SHARED_LOGIN_FORCE',
  personal_login_unavailable: 'read as on: Google sign-in is not fully set up on the server',
  last_good: 'the row could not be read just now; this is the last value read',
  unreadable_cold: 'the row could not be read',
  invalid: 'the row holds a value the switch does not know',
};
const REFUSAL_WORDS = {
  owner_google_unproven: 'Turning it off needs an active owner who has signed in with Google first. Sign in with Google, then try again.',
  personal_login_unavailable: 'Google sign-in is not fully set up on the server, so the shared login cannot be turned off yet.',
  owner_only: 'Only the owner, signed in with Google, can change this switch.',
  identity_unavailable: 'The sign-in check is unavailable right now. Try again shortly.',
};
const list = (xs) => (Array.isArray(xs) && xs.length ? xs.join(', ') : 'none'); // owner_surfaces only (strings)

export default function SignInAdmin() {
  const me = useMe();
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null); // the mode awaiting a second yes
  const [reasons, setReasons] = useState([]); // the 409's own reasons, in words

  const load = useCallback(async () => {
    try { setS(await getSharedLogin()); setError(''); }
    catch (e) { setError(REFUSAL_WORDS[e?.body?.code] || 'The switch could not be read.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function set(mode, confirm = false) {
    setSaving(true);
    try {
      const after = await setSharedLogin(mode, confirm);
      setConfirming(null);
      setS(after);
      if (after && after.mode === mode) toast.success(`The shared login is now ${labelOf(mode)}.`);
      else toast.error(`The switch reads ${labelOf(after && after.mode)} after the save, not ${labelOf(mode)}.`);
    } catch (e) {
      const code = e?.body?.code;
      if (code === 'confirm_required') { if (e.body && e.body.mode) setS(e.body); setReasons(confirmReasonLines(e.body)); setConfirming(mode); }
      else toast.error(REFUSAL_WORDS[code] || 'The switch did not change.');
    } finally {
      setSaving(false);
    }
  }

  if (me && !ownerPagesVisible(me)) {
    return <div className="p-4 text-sm text-text-muted">Only the owner sees this page.</div>;
  }

  return (
    <div className="max-w-3xl p-4">
      <h2 className="text-lg font-semibold text-text">Sign-in</h2>
      <p className="mt-1 text-sm text-text-muted">The shared TRACON password, beside everyone's own Google sign-in.</p>
      {error && <p className="mt-3 text-sm text-accent">{error}</p>}
      {!s && !error && <p className="mt-3 text-sm text-text-muted">Loading...</p>}
      {s && (
        <div className="mt-4 rounded-card border border-border bg-surface p-3">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="text-sm font-semibold text-text">Shared login</span>
            <span className="text-[12px] text-text-muted">now {labelOf(s.mode)}{WHY_WORDS[s.why] ? ` (${WHY_WORDS[s.why]})` : s.why && s.why !== 'row' ? ` (${s.why})` : ''}</span>
          </div>
          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[12.5px] text-text-muted">
            <dt>Owner signed in with Google</dt><dd>{s.owner_google_proven ? 'yes' : 'not yet'}</dd>
            <dt>Owner sign-ins on</dt><dd>{list((s.owner_surfaces || []).map((x) => (x === 'standalone' ? 'installed app' : x)))}</dd>
            <dt>Shared uses, 7 days</dt><dd>{Number.isFinite(s.uses_7d) ? s.uses_7d : 'unknown'}</dd>
            <dt>Last shared use</dt><dd>{s.last_use_at ? formatDateTime(s.last_use_at) : 'none recorded'}</dd>
            <dt>Routes used</dt><dd className="break-all">{sharedRoutesText(s.routes_7d, formatDateTime)}</dd>
            <dt>From</dt><dd className="break-all">{sharedOriginsText(s.origins_7d, formatDateTime)}</dd>
          </dl>
          {me && me.kind === 'shared' && (
            <p className="mt-2 text-[12.5px] text-text-muted">You are on the shared login. Only the owner's own Google sign-in can change this switch.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Shared login switch">
            {MODES.map((m) => (
              <button
                key={m.mode}
                type="button"
                role="radio"
                aria-checked={s.mode === m.mode}
                title={m.note}
                disabled={saving || s.mode === m.mode}
                onClick={() => set(m.mode)}
                className={[
                  'h-9 min-w-[5.5rem] rounded-card border px-3 text-sm disabled:cursor-not-allowed',
                  s.mode === m.mode ? 'border-accent bg-accent text-white' : 'border-border text-text hover:border-accent',
                ].join(' ')}
              >
                {m.label}
              </button>
            ))}
          </div>
          <ul className="mt-2 text-[12px] text-text-dim">
            {MODES.map((m) => <li key={m.mode}>{m.label}: {m.note}</li>)}
          </ul>
        </div>
      )}
      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Turn the shared login {labelOf(confirming)}?</DialogTitle>
            <DialogDescription>
              {reasons.map((line) => `${line} `)}
              The way back is the Render setting TRACON_SHARED_LOGIN_FORCE=on.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button disabled={saving} onClick={() => set(confirming, true)}>Turn it {labelOf(confirming)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
